import React from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { formatPercent, classNames } from '@/lib/utils';
import type { Segment, BacklightStatus } from '@/types';

interface MeasurementData {
    segmentId: string;
    segmentName: string;
    targetShare: number;
    measuredShare: number;
    imageUrl?: string;
}

interface BacklightData {
    status: BacklightStatus;
    imageUrl: string;
    notes?: string;
}

interface AuditSummaryProps {
    storeName: string;
    measurements: MeasurementData[];
    backlight?: BacklightData;
    onConfirm: () => void;
    onBack: () => void;
    isSubmitting?: boolean;
}

const BACKLIGHT_LABELS: Record<BacklightStatus, { label: string; icon: string; color: string }> = {
    implemented: { label: 'Implementado', icon: '✅', color: 'text-status-success' },
    not_implemented: { label: 'Não Implementado', icon: '❌', color: 'text-status-danger' },
    defective: { label: 'Com Defeito', icon: '⚠️', color: 'text-status-warning' },
};

export function AuditSummary({
    storeName,
    measurements,
    backlight,
    onConfirm,
    onBack,
    isSubmitting = false
}: AuditSummaryProps) {
    const compliantCount = measurements.filter(m => m.measuredShare >= m.targetShare).length;
    const compliancePercent = (compliantCount / measurements.length) * 100;
    const totalPhotos = measurements.filter(m => m.imageUrl).length + (backlight ? 1 : 0);

    return (
        <div className="space-y-6">
            {/* Header */}
            <Card>
                <CardContent>
                    <div className="text-center">
                        <h2 className="text-xl font-semibold text-content-title">
                            Revisão da Auditoria
                        </h2>
                        <p className="text-content-muted mt-1">{storeName}</p>
                    </div>
                </CardContent>
            </Card>

            {/* Resumo de compliance */}
            <Card>
                <CardContent>
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-medium text-content-muted">Compliance Share</span>
                        <span className={classNames(
                            'text-2xl font-bold',
                            compliancePercent >= 85 ? 'text-status-success' :
                                compliancePercent >= 70 ? 'text-status-warning' :
                                    'text-status-danger'
                        )}>
                            {formatPercent(compliancePercent, 0)}
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className={classNames(
                                'h-2 rounded-full transition-all',
                                compliancePercent >= 85 ? 'bg-status-success' :
                                    compliancePercent >= 70 ? 'bg-status-warning' :
                                        'bg-status-danger'
                            )}
                            style={{ width: `${compliancePercent}%` }}
                        />
                    </div>
                    <p className="text-sm text-content-muted mt-2 text-center">
                        {compliantCount} de {measurements.length} segmentos em compliance
                    </p>
                </CardContent>
            </Card>

            {/* Lista de segmentos */}
            <Card>
                <CardContent>
                    <h3 className="text-sm font-medium text-content-muted mb-3">Segmentos Auditados</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                        {measurements.map((m) => {
                            const isCompliant = m.measuredShare >= m.targetShare;
                            return (
                                <div
                                    key={m.segmentId}
                                    className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                                >
                                    <div className="flex items-center gap-2">
                                        <span className={classNames(
                                            'w-2 h-2 rounded-full',
                                            isCompliant ? 'bg-status-success' : 'bg-status-danger'
                                        )} />
                                        <span className="text-sm text-content-body">{m.segmentName}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={classNames(
                                            'text-sm font-medium',
                                            isCompliant ? 'text-status-success' : 'text-status-danger'
                                        )}>
                                            {formatPercent(m.measuredShare, 0)}
                                        </span>
                                        <span className="text-xs text-content-muted">
                                            / {formatPercent(m.targetShare, 0)}
                                        </span>
                                        {m.imageUrl && (
                                            <svg className="w-4 h-4 text-content-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Backlight */}
            {backlight && (
                <Card>
                    <CardContent>
                        <h3 className="text-sm font-medium text-content-muted mb-3">Backlight</h3>
                        <div className="flex items-center gap-3">
                            <span className="text-2xl">{BACKLIGHT_LABELS[backlight.status].icon}</span>
                            <div>
                                <p className={classNames(
                                    'font-medium',
                                    BACKLIGHT_LABELS[backlight.status].color
                                )}>
                                    {BACKLIGHT_LABELS[backlight.status].label}
                                </p>
                                {backlight.notes && (
                                    <p className="text-sm text-content-muted">{backlight.notes}</p>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Estatísticas */}
            <Card>
                <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-center">
                        <div>
                            <p className="text-2xl font-bold text-content-title">{measurements.length}</p>
                            <p className="text-sm text-content-muted">Segmentos</p>
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-content-title">{totalPhotos}</p>
                            <p className="text-sm text-content-muted">Fotos</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Botões */}
            <div className="flex gap-3">
                <Button variant="secondary" size="lg" fullWidth onClick={onBack} disabled={isSubmitting}>
                    Voltar
                </Button>
                <Button variant="primary" size="lg" fullWidth onClick={onConfirm} isLoading={isSubmitting}>
                    Confirmar e Enviar
                </Button>
            </div>
        </div>
    );
}

export default AuditSummary;
